import React, { useMemo } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { InView } from 'react-intersection-observer'
import { CaptionLG, ParagraphSM } from '@/atoms/Text'
import { GuildOfferPackage } from '@/constants/guild'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { OmniGuildPlanBox } from '@/views/GuildJoinView/OmniGuildPlanSelectionCard/OmniGuildPlanBox'
import { OfferTier } from '@/views/GuildJoinView/OmniGuildPlanSelectionCard/OmniGuildPlanBox/OmniGuildPlanBox'

interface Props {
  className?: string
  containerRef?: React.Ref<HTMLDivElement>
  darkMode?: boolean
  signupLayout?: boolean
  hideLabels?: boolean
  disclosePriceInUSD?: boolean
  isSignupLayout?: boolean
  guildOfferPackage: GuildOfferPackage
  landing?: boolean
  defaultSelectedOffer?: OfferTier
}

export const OmniGuildPlanSelectionCard: React.FC<Props> = ({
  className,
  containerRef,
  darkMode = false,
  signupLayout = false,
  hideLabels = false,
  disclosePriceInUSD = false,
  guildOfferPackage,
  defaultSelectedOffer,
}) => {
  const { t } = useTranslate('guild')
  const { query } = useSafeRouter()

  const { trackProductViewed } = useEcommerceEvents()

  const offers = useMemo(() => {
    const premium = guildOfferPackage['premium']
    const basic = guildOfferPackage['basic']
    const basicNoAds = guildOfferPackage['basicNoAds']
    const result = []

    if (premium) {
      result.push({
        id: 'premium',
        ...premium,
      })
    }

    if (basicNoAds) {
      result.push({
        id: 'basicNoAds',
        ...basicNoAds,
      })
    }

    if (basic) {
      result.push({
        id: 'basic',
        ...basic,
      })
    }

    return result
  }, [guildOfferPackage])
  return (
    <InView
      triggerOnce
      onChange={(inView) => {
        if (!inView) return
        const projectSlug = query.slug && String(query.slug)

        trackProductViewed({
          category: 'guild',
          funnel: 'guild',
          name: 'Angel Guild Plans',
          price: 0,
          currency: 'USD',
          quantity: 1,
          product_id: 'Angel Guild Plans',
          sku: 'Angel Guild Plans',
          projectSlug,
          variant: 'basic monthly, basic yearly, premium monthly, premium yearly',
        })
      }}
      className="mx-auto flex flex-col items-center"
    >
      <When condition={!hideLabels}>
        {!signupLayout && (
          <CaptionLG
            color={darkMode ? 'white' : 'core-gray-950'}
            weight={signupLayout ? 'semibold' : 'bold'}
            className={classNames('pb-4', { '!photon-title-md': signupLayout })}
          >
            {t('selectMembershipPlans', 'Select membership plans')}.
          </CaptionLG>
        )}
      </When>

      <When condition={disclosePriceInUSD}>
        <ParagraphSM weight="semibold" color="core-gray-800" className="md:pb-4 lg:-mt-4">
          *{t('pricesInUsd', 'Prices in USD')}
        </ParagraphSM>
      </When>
      <div ref={containerRef} className="w-full">
        <OmniGuildPlanBox
          className={className}
          offers={offers}
          disclosePriceInUSD={disclosePriceInUSD}
          defaultSelectedOffer={defaultSelectedOffer}
        />
      </div>
    </InView>
  )
}
